/**
 * Citations, rendered from the record the clipping froze.
 *
 * A clipping stores a *record* rather than rendered prose: publication,
 * publisher, place, headline, author, date, page, permalink and the date it
 * was accessed (`_freeze_citation` in `api/research.py`). That is deliberate.
 * Enrichment keeps running over this archive, so a byline gets corrected and
 * an article gets re-segmented; a researcher who cited a passage in March must
 * find the same citation in September. The record is frozen at creation and
 * never recomputed.
 *
 * The API now renders the styles itself: a clipping's frozen `citation` carries
 * `mla`, `chicago`, `apa`, `bibtex`, `ris` and `csl_json` alongside the record
 * they were rendered from. `renderStyle` therefore prefers the server's own
 * rendering, and everything below it is the **fallback** for a record that
 * arrives without one (an older server, or a style the API does not render).
 *
 * Keeping the fallback is worth the code: a citation is the one thing on this
 * screen a researcher will paste into work that outlives the deployment, so a
 * missing string should degrade to a composed citation rather than to a blank.
 * Nothing here invents a field the record does not carry: a missing author,
 * place or column label is left out rather than filled in.
 */

export type CitationStyle = 'mla' | 'chicago' | 'apa' | 'bibtex' | 'ris' | 'csl-json'

export const CITATION_STYLES: { value: CitationStyle; label: string }[] = [
  { value: 'mla', label: 'MLA 9' },
  { value: 'chicago', label: 'Chicago 18' },
  { value: 'apa', label: 'APA 7' },
  { value: 'bibtex', label: 'BibTeX' },
  { value: 'ris', label: 'RIS' },
  { value: 'csl-json', label: 'CSL-JSON' },
]

export const STYLE_NOTE: Record<CitationStyle, string> = {
  mla: 'MLA 9 · works cited',
  chicago: 'Chicago 18 · notes and bibliography',
  apa: 'APA 7 · reference list',
  bibtex: 'BibTeX · one entry per clipping',
  ris: 'RIS · one record per clipping',
  'csl-json': 'CSL-JSON · one object per clipping',
}

/** The frozen record, as `_freeze_citation` writes it. Every field may be null. */
export interface CitationRecord {
  publication?: string | null
  publisher?: string | null
  place?: string | null
  headline?: string | null
  author?: string | null
  date?: string | null
  page?: string | null
  permalink?: string | null
  accessed?: string | null
  /** Present on unit and article citations, absent on a clipping's. */
  columns_label?: string | null
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const MLA_MONTHS = [
  'Jan.',
  'Feb.',
  'Mar.',
  'Apr.',
  'May',
  'June',
  'July',
  'Aug.',
  'Sept.',
  'Oct.',
  'Nov.',
  'Dec.',
]

function parts(iso: string | null | undefined) {
  if (!iso) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

/** "August 18, 2026". */
export function longDate(iso: string | null | undefined): string | null {
  const p = parts(iso)
  return p ? `${MONTHS[p.month - 1]} ${p.day}, ${p.year}` : null
}

/** "18 Aug. 2026", the way MLA prints one. */
export function mlaDate(iso: string | null | undefined): string | null {
  const p = parts(iso)
  return p ? `${p.day} ${MLA_MONTHS[p.month - 1]} ${p.year}` : null
}

const SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'from',
  'in',
  'nor',
  'of',
  'on',
  'or',
  'the',
  'to',
  'up',
  'with',
])

/**
 * Headline-style capitalisation, for the styles that ask for it.
 *
 * Conservative on purpose: a word that already carries a capital after its
 * first letter is an acronym or a name the recognition read that way, and it
 * is left exactly as the paper printed it.
 */
export function headlineCase(text: string): string {
  const words = text.split(/\s+/)
  return words
    .map((word, index) => {
      if (/[A-Z]/.test(word.slice(1))) return word
      const bare = word.toLowerCase()
      if (index > 0 && index < words.length - 1 && SMALL_WORDS.has(bare.replace(/[^a-z]/g, ''))) {
        return bare
      }
      return bare.charAt(0).toUpperCase() + bare.slice(1)
    })
    .join(' ')
}

/**
 * The byline as a citation wants a name.
 *
 * `byline_raw` is what the paper printed, which on this title is "BY DOUG
 * DIACZUK" in capitals with the credit line's punctuation still attached. The
 * raw value is never altered in the archive; this is the citation's own
 * rendering of it, and it only strips the credit word and the trailing
 * punctuation and un-shouts a name set entirely in capitals.
 */
function cleanAuthor(name: string): string {
  let clean = name.trim().replace(/\s+/g, ' ')
  clean = clean.replace(/^by\s+/i, '')
  clean = clean.replace(/[,;.\s]+$/, '')
  if (clean && clean === clean.toUpperCase()) {
    clean = clean
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return clean
}

/** "Winters, Cindy" from "Cindy Winters", for the styles that invert a name. */
function invertName(name: string): string {
  const clean = cleanAuthor(name)
  if (!clean || clean.includes(',')) return clean
  const bits = clean.split(' ')
  if (bits.length < 2) return clean
  const family = bits.pop() as string
  return `${family}, ${bits.join(' ')}`
}

function initials(name: string): string {
  const clean = cleanAuthor(name)
  const bits = clean.split(' ')
  if (bits.length < 2) return clean
  const family = bits.pop() as string
  return `${family}, ${bits.map((bit) => `${bit.charAt(0).toUpperCase()}.`).join(' ')}`
}

/** Strip the scheme so a permalink reads as a citation rather than a URL bar. */
function shortLink(url: string | null | undefined): string | null {
  if (!url) return null
  return url.replace(/^https?:\/\//, '')
}

function join(bits: (string | null | undefined)[], separator = ' '): string {
  return bits.filter((bit) => bit !== null && bit !== undefined && bit !== '').join(separator)
}

function citeKey(record: CitationRecord, index: number): string {
  const author = record.author ? cleanAuthor(record.author).split(/\s+/).pop() ?? '' : ''
  const year = parts(record.date)?.year ?? ''
  const word = (record.headline ?? 'clipping').split(/\s+/)[0].replace(/[^A-Za-z]/g, '')
  return `${(author || word).toLowerCase()}${year}${index > 0 ? String(index + 1) : ''}` || 'clipping'
}

export interface RenderContext {
  /** `settings.PRODUCT_NAME`, never a literal in this source tree. */
  productName: string
  index?: number
}

export function renderCitation(
  record: CitationRecord,
  style: CitationStyle,
  context: RenderContext,
): string {
  const titled = !!record.headline?.trim()
  // An item the archive holds no headline for is described rather than given
  // an invented one, and a description is not set in quotation marks.
  const title = record.headline?.trim() || 'Untitled clipping'
  const quoted = titled ? `“${headlineCase(title)}.”` : `${title}.`
  const publication = record.publication ?? null
  const place = record.place ?? null
  const page = record.page ?? null
  const columns = record.columns_label ?? null
  const link = shortLink(record.permalink)
  const p = parts(record.date)

  switch (style) {
    case 'mla': {
      const author = record.author ? `${invertName(record.author)}. ` : ''
      return join(
        [
          `${author}${quoted}`,
          publication ? `${publication},` : null,
          mlaDate(record.date) ? `${mlaDate(record.date)},` : null,
          page ? `p. ${page}.` : null,
          link ? `${context.productName}, ${link}.` : null,
          record.accessed ? `Accessed ${mlaDate(record.accessed)}.` : null,
        ],
        ' ',
      )
    }
    case 'chicago': {
      const author = record.author ? `${invertName(record.author)}. ` : ''
      const where = join([page, columns], ', ')
      return join(
        [
          `${author}${quoted}`,
          publication ? (place ? `${publication} (${place}),` : `${publication},`) : null,
          longDate(record.date) ? `${longDate(record.date)},` : null,
          where ? `${where}.` : null,
          link ? `${context.productName}, ${link}.` : null,
        ],
        ' ',
      )
    }
    case 'apa': {
      const author = record.author ? `${initials(record.author)} ` : ''
      const when = p ? `(${p.year}, ${MONTHS[p.month - 1]} ${p.day}).` : '(n.d.).'
      return join(
        [
          `${author}${when}`,
          `${title}.`,
          publication ? `${publication}` : null,
          page ? `, ${page}.` : '.',
          record.permalink ?? null,
        ],
        ' ',
      ).replace(' , ', ', ')
    }
    case 'bibtex': {
      const key = citeKey(record, context.index ?? 0)
      const lines = [
        `@article{${key},`,
        `  title     = {${title}},`,
        record.author ? `  author    = {${cleanAuthor(record.author)}},` : null,
        publication ? `  journal   = {${publication}},` : null,
        p ? `  year      = {${p.year}},` : null,
        p ? `  month     = {${MONTHS[p.month - 1].slice(0, 3).toLowerCase()}},` : null,
        p ? `  day       = {${p.day}},` : null,
        page ? `  pages     = {${page}},` : null,
        place ? `  address   = {${place}},` : null,
        record.permalink ? `  url       = {${record.permalink}},` : null,
        record.accessed ? `  urldate   = {${record.accessed}},` : null,
        `  note      = {${context.productName}},`,
        '}',
      ]
      return lines.filter(Boolean).join('\n')
    }
    case 'ris': {
      const lines = [
        'TY  - NEWS',
        `TI  - ${title}`,
        record.author ? `AU  - ${cleanAuthor(record.author)}` : null,
        publication ? `JO  - ${publication}` : null,
        p ? `DA  - ${p.year}/${String(p.month).padStart(2, '0')}/${String(p.day).padStart(2, '0')}` : null,
        p ? `PY  - ${p.year}` : null,
        page ? `SP  - ${page}` : null,
        place ? `CY  - ${place}` : null,
        record.publisher ? `PB  - ${record.publisher}` : null,
        record.permalink ? `UR  - ${record.permalink}` : null,
        record.accessed ? `Y2  - ${record.accessed}` : null,
        `DP  - ${context.productName}`,
        'ER  - ',
      ]
      return lines.filter(Boolean).join('\n')
    }
    case 'csl-json': {
      const object: Record<string, unknown> = {
        id: citeKey(record, context.index ?? 0),
        type: 'article-newspaper',
        title,
      }
      if (record.author) {
        const bits = cleanAuthor(record.author).split(/\s+/)
        const family = bits.length > 1 ? bits.pop() : cleanAuthor(record.author)
        object.author = [{ family, given: bits.join(' ') || undefined }]
      }
      if (publication) object['container-title'] = publication
      if (place) object['publisher-place'] = place
      if (record.publisher) object.publisher = record.publisher
      if (page) object.page = page
      if (p) object.issued = { 'date-parts': [[p.year, p.month, p.day]] }
      if (record.permalink) object.URL = record.permalink
      const accessed = parts(record.accessed)
      if (accessed) {
        object.accessed = { 'date-parts': [[accessed.year, accessed.month, accessed.day]] }
      }
      object.source = context.productName
      return JSON.stringify(object, null, 2)
    }
  }
}

/**
 * Prefer what the API rendered, compose what it did not.
 *
 * Clippings, articles and units all arrive with the six styles rendered
 * server-side, `csl_json` as an object rather than a string. One rendering,
 * done once, beside the frozen record is what keeps a footnote stable, so the
 * server's string wins wherever there is one and `renderCitation` is only the
 * fallback.
 */
export function renderStyle(
  record: CitationRecord & Record<string, unknown>,
  style: CitationStyle,
  context: RenderContext,
): string {
  const key = style === 'csl-json' ? 'csl_json' : style
  const supplied = record[key]
  if (typeof supplied === 'string' && supplied.trim()) return supplied
  // CSL-JSON is a document, not a line of prose, and comes back as an object.
  if (supplied && typeof supplied === 'object') return JSON.stringify(supplied, null, 2)
  return renderCitation(record, style, context)
}

/** Whether this style came from the API rather than from the fallback above. */
export function isServerRendered(
  record: CitationRecord & Record<string, unknown>,
  style: CitationStyle,
): boolean {
  const supplied = record[style === 'csl-json' ? 'csl_json' : style]
  if (typeof supplied === 'string') return supplied.trim().length > 0
  return !!supplied && typeof supplied === 'object'
}

/** The file a style is taken away as. */
export const STYLE_FILE: Partial<Record<CitationStyle, { extension: string; mime: string }>> = {
  bibtex: { extension: 'bib', mime: 'application/x-bibtex' },
  ris: { extension: 'ris', mime: 'application/x-research-info-systems' },
  'csl-json': { extension: 'json', mime: 'application/json' },
}

/** A CSV of the frozen records, which is what a spreadsheet wants. */
export function recordsToCsv(records: CitationRecord[]): string {
  const columns = [
    'headline',
    'author',
    'publication',
    'place',
    'date',
    'page',
    'permalink',
    'accessed',
  ] as const
  const escape = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  return [
    columns.join(','),
    ...records.map((record) => columns.map((column) => escape(record[column])).join(',')),
  ].join('\n')
}
